import { useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useRBAC } from '../../../contexts/RBACContext';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { fetchBpTickets } from '../services/bpTicketsApi';
import type { BpTicket } from '../types/bpTickets';
import { TICKET_STATUSES } from '../types/bpTickets';
import { CategoryBadge, PriorityBadge, StatusBadge } from '../components/ticketing/badges';
import { MultiSelect } from '../components/ticketing/MultiSelect';
import { usePeopleTicketsAccess } from '../components/peopleTickets/usePeopleTicketsAccess';
import { PeopleMyInquiries } from './peopleTickets/PeopleMyInquiries';

// TEMPORARY (Sept 2026 beta): see the matching note in people-space/config.ts — remove this
// gate together with the nav item's requiredPermission once the beta is confirmed working.
const BETA_GATE_PERMISSION = 'space.people.ticket_management.view';

const DEFAULT_STATUSES = ['Submitted', 'In Progress', 'Waiting for Employee'];

function BetaAccessRestricted() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
          <Lock className="h-6 w-6 text-zinc-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Coming soon</h2>
          <p className="mt-1 text-sm text-zinc-500">
            My Inquiries is in a limited beta right now. It'll be available to everyone shortly.
          </p>
        </div>
      </div>
    </div>
  );
}

function MyInquiriesContent() {
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();

  const [myTickets, setMyTickets] = useState<BpTicket[]>([]);
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set(DEFAULT_STATUSES));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBpTickets()
      .then((all) => setMyTickets(all.filter((t) => t.submittedBy.toLowerCase() === myEmail)))
      .finally(() => setLoading(false));
  }, [myEmail]);

  const tickets = useMemo(
    () => myTickets.filter((t) => statusFilter.size === 0 || statusFilter.has(t.status)),
    [myTickets, statusFilter],
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" aria-label="Loading your inquiries" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">My inquiries</h1>
          <p className="mt-0.5 text-sm text-gray-500">Signed in as {user?.email}</p>
        </div>
        <MultiSelect
          label="Status"
          options={TICKET_STATUSES.map((s) => ({ value: s, label: s }))}
          selected={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      <ul className="list-none space-y-3">
        {tickets.map((t) => (
          <li key={t.id} className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900">{t.summary}</p>
              <StatusBadge status={t.status} />
            </div>
            <p className="mb-2 text-sm text-gray-700">{t.details}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <CategoryBadge category={t.category} />
              <PriorityBadge priority={t.priority} />
              <span className="text-xs text-gray-500">
                Last update {t.lastChangedAt ? new Date(t.lastChangedAt).toLocaleDateString() : 'unknown'}
              </span>
              <span className="ml-auto text-xs text-gray-500">Business Partner: {t.dri?.name || '—'}</span>
            </div>
          </li>
        ))}
        {tickets.length === 0 && <p className="text-gray-500">No inquiries match this filter.</p>}
      </ul>
    </div>
  );
}

export default function MyInquiriesView() {
  const { loading: rbacLoading, permissions, isCoreAdmin } = useRBAC();
  const { access, loading: accessLoading } = usePeopleTicketsAccess();
  const loading = rbacLoading || accessLoading;

  const allowed = isCoreAdmin || permissions.has('*') || permissions.has(BETA_GATE_PERMISSION);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" aria-label="Checking access" />
      </div>
    );
  }

  // People Tickets is live for everyone (Oct 2026): their own requests from the merged table.
  // Only an EXPLICIT "not enabled" from the proxy (the pilot gate re-armed) falls back to the old
  // pages — an /access call that failed must not read as "Restricted" to a colleague it was
  // simply unable to ask about. The new page reports its own load error.
  if (access?.enabled !== false) return <PeopleMyInquiries />;
  if (!allowed) return <BetaAccessRestricted />;

  return <MyInquiriesContent />;
}
